import {
  Typography,
  Box,
  useTheme,
  CardContent,
  TextField,
  Tooltip,
  Divider,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  FormHelperText,
} from '@mui/material';
import { Controller } from 'react-hook-form';
import { Info } from 'lucide-react';

interface BookingSettingsProps {
  control: any;
  isEditing: boolean;
}

const BookingSettings = ({ control, isEditing }: BookingSettingsProps) => {
  const theme = useTheme();

  return (
    <Box
      className="w-full xl:max-w-4xl mx-auto mt-10"
      sx={{
        backgroundColor: theme.palette.background.paper,
      }}
    >
      <Box sx={{ mb: 0 }}>
        <Box display="flex" alignItems="center">
          <Typography variant="h6">Booking Settings</Typography>
          <Tooltip title="Booking-related settings for your organization">
            <Info size={16} style={{ marginLeft: '8px' }} />
          </Tooltip>
        </Box>
        <Typography variant="body2" color="textSecondary">
          Configure your organization's booking preferences and appearance
        </Typography>
      </Box>
      <Divider sx={{ my: 2 }} />
      <CardContent sx={{ p: 0 }}>
        <Controller
          name="resourceProfessional"
          control={control}
          rules={{ required: 'Resource profession is required' }}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              label="Resource Profession"
              fullWidth
              value={field.value === false ? '' : field.value}
              error={!!error}
              helperText={error?.message}
              placeholder="e.g., Doctors, Hairdressers, Therapists"
              disabled={!isEditing}
            />
          )}
        />

        <Controller
          name="isVerifyBySMS"
          control={control}
          rules={{
            validate: value => value === true || value === false || 'Validate customer by?',
          }}
          render={({ field, fieldState: { error } }) => (
            <FormControl component="fieldset" error={!!error} sx={{ mt: 2 }}>
              <FormLabel component="legend">
                Customer Verification Method
                <span
                  style={{
                    fontSize: '0.8em',
                    color: theme.palette.text.disabled,
                    marginLeft: '4px',
                    fontStyle: 'italic',
                    display: 'block',
                    marginTop: '4px',
                  }}
                >
                  (This will capture customer phone or email as part of verification)
                </span>
              </FormLabel>
              <RadioGroup
                row
                value={field.value === true ? 'true' : 'false'}
                onChange={e => field.onChange(e.target.value === 'true')}
                name="isVerifyBySMS"
              >
                <FormControlLabel
                  value="true"
                  control={<Radio />}
                  label="SMS"
                  disabled={!isEditing}
                />
                <FormControlLabel
                  value="false"
                  control={<Radio />}
                  label="Email"
                  disabled={!isEditing}
                />
              </RadioGroup>
              {error && <FormHelperText>{error.message}</FormHelperText>}
            </FormControl>
          )}
        />

        <Controller
          name="bookingDescription"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              multiline
              rows={4}
              label="Description"
              placeholder="Enter a description for your booking page"
              helperText="This description will be shown to customers on your booking page"
              sx={{ mt: 2, mb: 4 }}
              disabled={!isEditing}
            />
          )}
        />
      </CardContent>
    </Box>
  );
};

export default BookingSettings;
